/**
 * Bounded retry for the boot-time `/auth/me` sync. A transient failure (429
 * from the shared rate bucket, a 5xx, a network blip) must never destroy a
 * valid Supabase session — it gets a short backoff ladder instead; only a
 * definitive 401 kills the local auth state.
 */
import { classifyError } from '@stoxly/api-client';

export const BOOT_SYNC_MAX_ATTEMPTS = 4;

/** Backoff before retry N+1 (attempt 1 → wait delays[0] → attempt 2 → ...). */
export const BOOT_SYNC_RETRY_DELAYS_MS = [1000, 2000, 4000] as const;

export type BootSyncOutcome<T> =
  | { status: 'ok'; value: T }
  /** The backend definitively rejected the token (401) — the session is dead. */
  | { status: 'unauthorized'; error: unknown }
  /** Recoverable failures exhausted the ladder, or a fatal non-auth error. */
  | { status: 'failed'; error: unknown };

interface RunWithBootRetryOptions {
  /** Called with the 1-based attempt number before it runs (drives boot copy). */
  onAttempt?: (attempt: number) => void;
  /** Injectable delay, for specs. */
  sleep?: (ms: number) => Promise<void>;
}

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

export async function runWithBootRetry<T>(
  operation: () => Promise<T>,
  { onAttempt, sleep = defaultSleep }: RunWithBootRetryOptions = {},
): Promise<BootSyncOutcome<T>> {
  for (let attempt = 1; ; attempt++) {
    onAttempt?.(attempt);
    try {
      return { status: 'ok', value: await operation() };
    } catch (error) {
      const classified = classifyError(error);
      if (classified.status === 401) {
        return { status: 'unauthorized', error };
      }
      if (!classified.recoverable || attempt >= BOOT_SYNC_MAX_ATTEMPTS) {
        return { status: 'failed', error };
      }
      await sleep(BOOT_SYNC_RETRY_DELAYS_MS[attempt - 1]);
    }
  }
}
